import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Search, ShoppingCart, User, ChevronDown, Menu, X, Pill, Activity, Upload, Heart, Package, LogOut, LayoutDashboard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_9d18eba3-f81c-4175-a241-09119da09ef9/artifacts/lx9rjtoy_WhatsApp%20Image%202026-03-01%20at%204.03.51%20PM.jpeg";

const MEGA_MENU = [
  { name: "Medicines", subs: ["Prescription", "OTC", "Ayurvedic", "Homeopathic", "Vitamins & Supplements"] },
  { name: "Personal Care", subs: ["Skin Care", "Hair Care", "Oral Care", "Baby Care", "Women Care"] },
  { name: "Health Conditions", subs: ["Diabetes", "Heart", "Thyroid", "Ortho", "Respiratory"] },
  { name: "Devices", subs: ["BP Monitor", "Glucometer", "Thermometer", "Nebulizer"] },
];

export default function Navbar() {
  const { user, logout, cartCount } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showMega, setShowMega] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchRef = useRef(null);
  const megaRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length > 1) {
        try {
          const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(searchQuery)}`);
          setSearchResults(res.data.results || []);
          setShowSearch(true);
        } catch { setSearchResults([]); }
      } else { setShowSearch(false); setSearchResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
      if (megaRef.current && !megaRef.current.contains(e.target)) setShowMega(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUser(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) { navigate(`/products?search=${encodeURIComponent(searchQuery)}`); setShowSearch(false); }
  };

  return (
    <nav className="sticky top-0 z-50 glass-nav shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Pharma Palace" className="h-10 w-10 rounded-xl object-cover" />
            <span className="hidden sm:block font-bold text-lg tracking-tight text-slate-800">Pharma <span className="text-cyan-600">Palace</span></span>
          </Link>

          {/* Categories Dropdown */}
          <div className="hidden lg:block relative" ref={megaRef}>
            <button onClick={() => setShowMega(!showMega)} className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:text-cyan-600 transition-colors rounded-xl hover:bg-cyan-50" data-testid="nav-categories-btn">
              <Menu className="w-4 h-4" /> Categories <ChevronDown className={`w-3 h-3 transition-transform ${showMega ? 'rotate-180' : ''}`} />
            </button>
            {showMega && (
              <div className="absolute top-full left-0 mt-2 w-[600px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.1)] border border-slate-100 p-6 animate-slideDown" data-testid="mega-menu">
                <div className="grid grid-cols-2 gap-6">
                  {MEGA_MENU.map(cat => (
                    <div key={cat.name}>
                      <Link to={`/products?category=${encodeURIComponent(cat.name)}`} onClick={() => setShowMega(false)} className="font-semibold text-slate-800 text-sm hover:text-cyan-600 transition-colors">{cat.name}</Link>
                      <div className="mt-2 space-y-1">
                        {cat.subs.map(sub => (
                          <Link key={sub} to={`/products?category=${encodeURIComponent(cat.name)}&subcategory=${encodeURIComponent(sub)}`} onClick={() => setShowMega(false)} className="block text-xs text-slate-500 hover:text-cyan-600 hover:pl-1 transition-all">{sub}</Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="flex-1 max-w-xl relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search medicines, health products..." data-testid="nav-search-input"
                className="w-full pl-10 pr-4 py-2.5 bg-sky-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder:text-slate-400 transition-all"
              />
            </form>
            {showSearch && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 max-h-80 overflow-auto animate-slideDown" data-testid="search-results">
                {searchResults.map(p => (
                  <Link key={p.product_id} to={`/products/${p.product_id}`} onClick={() => { setShowSearch(false); setSearchQuery(""); }}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-sky-50 transition-colors">
                    <img src={p.images?.[0] || ""} alt={p.name} className="w-10 h-10 rounded-xl object-cover bg-slate-100" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.brand}</p>
                    </div>
                    <span className="text-sm font-semibold text-cyan-600">Rs.{p.sale_price}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <Link to="/products" className="hidden md:inline-flex items-center gap-1 px-4 py-2 bg-cyan-600 text-white text-xs font-semibold rounded-full hover:bg-cyan-700 transition-all hover:shadow-lg" data-testid="nav-upload-rx">
              <Upload className="w-3.5 h-3.5" /> Upload Rx
            </Link>

            <Link to="/cart" className="relative p-2 text-slate-600 hover:text-cyan-600 transition-colors" data-testid="nav-cart-btn">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-cyan-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce-badge">{cartCount}</span>}
            </Link>

            {user ? (
              <div className="relative" ref={userRef}>
                <button onClick={() => setShowUser(!showUser)} className="flex items-center gap-2 p-1.5 rounded-full hover:bg-sky-50 transition-colors" data-testid="nav-user-btn">
                  {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-semibold text-sm">{user.name?.[0]?.toUpperCase()}</div>}
                </button>
                {showUser && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 animate-slideDown" data-testid="user-dropdown">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <Link to="/account" onClick={() => setShowUser(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-cyan-600"><User className="w-4 h-4" /> My Account</Link>
                    <Link to="/account/orders" onClick={() => setShowUser(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-cyan-600"><Package className="w-4 h-4" /> My Orders</Link>
                    <Link to="/account/wishlist" onClick={() => setShowUser(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-cyan-600"><Heart className="w-4 h-4" /> Wishlist</Link>
                    {user.role === "admin" && <Link to="/admin" onClick={() => setShowUser(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-cyan-600"><LayoutDashboard className="w-4 h-4" /> Admin Panel</Link>}
                    <button onClick={() => { logout(); setShowUser(false); navigate("/"); }} className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 w-full" data-testid="nav-logout-btn"><LogOut className="w-4 h-4" /> Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-cyan-600 border border-cyan-200 rounded-full hover:bg-cyan-50 transition-all" data-testid="nav-login-btn">
                <User className="w-4 h-4" /> Login
              </Link>
            )}

            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-600" data-testid="nav-mobile-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 py-4 px-4 animate-slideDown">
          {MEGA_MENU.map(cat => (
            <div key={cat.name} className="py-2">
              <Link to={`/products?category=${encodeURIComponent(cat.name)}`} onClick={() => setMobileOpen(false)} className="font-semibold text-sm text-slate-800">{cat.name}</Link>
              <div className="ml-4 mt-1 space-y-1">
                {cat.subs.slice(0, 3).map(sub => (
                  <Link key={sub} to={`/products?category=${encodeURIComponent(cat.name)}&subcategory=${encodeURIComponent(sub)}`} onClick={() => setMobileOpen(false)} className="block text-xs text-slate-500">{sub}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
